import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import ThemeSwitcher from "./ThemeSwitcher";

const FloatingNavigation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const location = useLocation();

  // Define sections based on current route
  const getSectionsForRoute = () => {
    const path = location?.pathname;

    if (path === "/services") {
      return [
        { id: "hero", label: "Service Hero", icon: "Star" },
        { id: "overview", label: "Overview", icon: "Eye" },
        { id: "methodology", label: "Methodology", icon: "Settings" },
        { id: "technology", label: "Technology", icon: "Code" },
        { id: "case-studies", label: "Case Studies", icon: "FileText" },
        { id: "testimonials", label: "Testimonials", icon: "MessageSquare" },
        { id: "cta", label: "Get Started", icon: "ArrowRight" },
      ];
    }

    if (path === "/home") {
      return [
        { id: "hero", label: "Hero", icon: "Home" },
        { id: "services", label: "Services", icon: "Grid3X3" },
        { id: "about", label: "About", icon: "Users" },
        { id: "contact", label: "Contact", icon: "Mail" },
      ];
    }

    if (path === "/about" || path === "/") {
      return [
        { id: "hero", label: "Hero", icon: "Users" },
        { id: "story", label: "Our Story", icon: "BookOpen" },
        { id: "team", label: "Team", icon: "Users" },
        { id: "partners", label: "Partners", icon: "Handshake" },
        { id: "testimonials", label: "Testimonials", icon: "MessageSquare" },
      ];
    }

    if (path === "/contact") {
      return [
        { id: "hero", label: "Contact", icon: "Mail" },
        { id: "form", label: "Get in Touch", icon: "Send" },
        { id: "info", label: "Contact Info", icon: "MapPin" },
      ];
    }

    if (path === "/application") {
      return [
        { id: "hero", label: "Application", icon: "FileText" },
        { id: "form", label: "Form", icon: "Edit3" },
        { id: "summary", label: "Summary", icon: "CheckCircle" },
      ];
    }

    // Default sections for other pages
    return [
      { id: "top", label: "Top", icon: "ArrowUp" },
      { id: "content", label: "Content", icon: "FileText" },
      { id: "bottom", label: "Bottom", icon: "ArrowDown" },
    ];
  };

  const sections = getSectionsForRoute();

  // Scroll spy to detect active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (let i = sections?.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections?.[i]?.id);
        if (section && scrollPosition >= section?.offsetTop) {
          setActiveSection(sections?.[i]?.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element?.offsetTop - offset;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
    setIsExpanded(false);
  };

  // Auto-collapse when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isExpanded]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed left-4 top-1/2 transform -translate-y-1/2 z-50"
      onClick={(e) => e?.stopPropagation()}
    >
      {/* Main Navigation Container */}
      <motion.div
        className={`bg-card/90 backdrop-blur-md border border-border rounded-2xl shadow-lg transition-all duration-300 ${
          isExpanded ? "px-4 py-3" : "p-2"
        }`}
        style={{
          boxShadow: isExpanded
            ? "0 0 30px rgba(246, 229, 92, 0.3)"
            : "0 0 20px rgba(246, 229, 92, 0.2)",
        }}
      >
        {/* Collapsed State - Toggle Button */}
        <AnimatePresence>
          {!isExpanded && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsExpanded(true)}
              className="w-10 h-10 flex items-center justify-center bg-accent/20 hover:bg-accent/30 rounded-xl transition-colors duration-200 group"
              aria-label="Open Navigation"
            >
              <Icon
                name="Navigation"
                size={20}
                className="text-accent group-hover:text-accent/80"
              />

              {/* Active section indicator */}
              <motion.div
                className="absolute -right-1 -top-1 w-3 h-3 bg-accent rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Expanded State - Navigation Menu */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col space-y-2 min-w-[220px]"
            >
              {/* Header with close button and theme switcher */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-foreground">
                  Navigate
                </span>
                <div className="flex items-center space-x-2">
                  <ThemeSwitcher
                    variant="toggle"
                    size="sm"
                    className="opacity-80 hover:opacity-100"
                  />
                  <button
                    onClick={() => setIsExpanded(false)}
                    className="w-6 h-6 flex items-center justify-center hover:bg-muted/50 rounded-full transition-colors duration-200"
                    aria-label="Close Navigation"
                  >
                    <Icon name="X" size={14} className="text-text-secondary" />
                  </button>
                </div>
              </div>

              {/* Navigation Items */}
              {sections?.map((section, index) => (
                <motion.button
                  key={section?.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  onClick={() => scrollToSection(section?.id)}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 text-left ${
                    activeSection === section?.id
                      ? "bg-accent/20 text-accent border border-accent/30 shadow-sm"
                      : "text-text-secondary hover:text-foreground hover:bg-muted/40"
                  }`}
                >
                  <div
                    className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                      activeSection === section?.id
                        ? "bg-accent/30"
                        : "bg-muted/40"
                    }`}
                  >
                    <Icon
                      name={section?.icon}
                      size={14}
                      className={
                        activeSection === section?.id
                          ? "text-accent"
                          : "text-text-secondary"
                      }
                    />
                  </div>
                  <span className="font-medium flex-1">{section?.label}</span>

                  {/* Active indicator */}
                  {activeSection === section?.id && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="w-2 h-2 bg-accent rounded-full"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.button>
              ))}

              {/* Divider */}
              <div className="border-t border-border my-2" />

              {/* Quick Actions */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-muted/40 hover:bg-accent/20 rounded-lg text-xs text-text-secondary hover:text-accent transition-colors duration-200"
                >
                  <Icon name="ArrowUp" size={12} />
                  <span>Top</span>
                </button>
                <button
                  onClick={() =>
                    window.scrollTo({
                      top: document.documentElement?.scrollHeight,
                      behavior: "smooth",
                    })
                  }
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-muted/40 hover:bg-accent/20 rounded-lg text-xs text-text-secondary hover:text-accent transition-colors duration-200"
                >
                  <Icon name="ArrowDown" size={12} />
                  <span>Bottom</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {/* Progress indicator */}
      <AnimatePresence>
        {!isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            className="mt-3 flex flex-col items-center"
          >
            <div className="w-1 h-16 bg-border rounded-full overflow-hidden">
              <motion.div
                className="w-full bg-gradient-to-b from-accent to-accent/50 rounded-full"
                style={{
                  height: `${Math.min(
                    100,
                    (window.scrollY /
                      (document.documentElement?.scrollHeight -
                        window.innerHeight)) *
                      100
                  )}%`,
                }}
                transition={{ duration: 0.1 }}
              />
            </div>
            <motion.div
              className="mt-2 text-xs text-text-secondary bg-card/90 backdrop-blur-sm px-2 py-1 rounded-lg border border-border shadow-sm"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              {Math.round(
                (window.scrollY /
                  (document.documentElement?.scrollHeight -
                    window.innerHeight)) *
                  100
              )}
              %
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FloatingNavigation;
